import React, { useCallback, useMemo } from 'react';
import { isNull } from 'lodash/fp';

import styles from '../Reports.module.scss';
import { ConfirmationModal } from '../../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import { Settings } from '../../../shared/icons';
import { DocumentReportField, updateReportFieldCustomLabel, updateReportFieldIncludeDatasetTitle } from '../store';
import { Text } from '../../../shared/text/Text';
import { Toggle } from '../../../shared/toggle';
import { OverflowTooltip } from '../../../shared/tooltip';
import { useAppDispatch } from '../../../../hooks/react-redux';

interface ConfigureColumnDetailsModalProps {
    closeModal: () => void;
    selectedColumn: DocumentReportField | null;
}

export const ConfigureColumnDetailsModal: React.FC<ConfigureColumnDetailsModalProps> = ({ closeModal, selectedColumn }) => {
    const dispatch = useAppDispatch();
    const displayColumn = useMemo(() => {
        if (isNull(selectedColumn)) {
            return '';
        }
        const { label, customLabel, fieldName, includeDatasetTitle } = selectedColumn;
        const columnLabel = customLabel || label;
        return `${columnLabel}${includeDatasetTitle ? ` (${fieldName})` : ''}`;
    }, [selectedColumn]);

    const updateCustomLabel = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isNull(selectedColumn)) {
            dispatch(updateReportFieldCustomLabel(`${selectedColumn.parentFieldId}-${selectedColumn.id}`, e.target.value));
        }
    }, [selectedColumn, dispatch]);

    const toggleIncludeDatasetTitle = useCallback((checked: boolean) => {
        if (!isNull(selectedColumn)) {
            dispatch(updateReportFieldIncludeDatasetTitle(`${selectedColumn.parentFieldId}-${selectedColumn.id}`, checked));
        }
    }, [selectedColumn, dispatch]);

    if (isNull(selectedColumn)) {
        return null;
    }

    const { label, customLabel, includeDatasetTitle } = selectedColumn;

    return (
        <ConfirmationModal
            isOpen
            closeModal={closeModal}
            closeLabel='Close'
            showConfirm={false}
            testId='document-report-configure'
        >
            <div className={styles.configureColumnDetailsModalWrapper}>
                <ModalHeader
                    label='Edit Column Header'
                    icon={Settings}
                />
                <div className={styles.configureColumnDetailsWrapper}>
                    <Text
                        value={customLabel}
                        onChange={updateCustomLabel}
                        testId='document-report-configure-custom-label'
                        label='Display Label'
                        placeholder={label}
                    />
                    <div className={styles.includeDatasetTitleWrapper}>
                        <div className={styles.includeDatasetTitleLabel}>Include Dataset Title</div>
                        <Toggle onChange={toggleIncludeDatasetTitle} checked={includeDatasetTitle} />
                    </div>
                    <div className={styles.displayColumnWrapper}>
                        <div className={styles.displayColumnLabel}>Column header when generated in report</div>
                        <div className={styles.displayColumn}><OverflowTooltip overlayText={displayColumn} /></div>
                    </div>
                </div>
            </div>
        </ConfirmationModal>
    );
};
